import { audioContext, decodeAudio } from './context'

/**
 * One-shot sound effects.
 *
 * A module singleton, not a hook: effects are fired from the store (spin
 * start, win), from the Pixi renderer (reel stops) and from React
 * components (buttons). Threading a player reference through all three
 * would mean plumbing in every one of them for something that has no
 * state worth sharing.
 *
 * Buffers are decoded once per theme and reused. Each `play` builds a
 * fresh BufferSource — that is the API's design, sources are single-use —
 * which also means the same effect can overlap with itself, exactly what
 * three reels stopping in quick succession need.
 *
 * Everything degrades to silence: an unknown name, a file that 404s, a
 * theme with no sounds at all. Effects are feedback, not mechanics.
 */

/** Effect names a theme may supply. Anything else in the manifest is ignored. */
export type SfxName =
  | 'button-click'
  | 'spin-start'
  | 'reel-spin'
  | 'reel-stop-1'
  | 'reel-stop-2'
  | 'reel-stop-3'
  | 'win-small'
  | 'win-big'
  | 'wild-hit'
  | 'gamble-flip'
  | 'gamble-win'
  | 'gamble-lose'
  // Tumble board: the cascade cycle and its events.
  | 'cascade-pop'
  | 'tile-land'
  | 'bomb-hit'
  | 'scatter-hit'
  | 'free-intro'
  // Stage characters — played when the player pokes the scenery.
  | 'ghost-boo'
  | 'ghost-woo'
  | 'cat-purr'
  | 'devil-hmm'

const buffers = new Map<string, AudioBuffer>()
/** Theme currently loaded, so switching games reloads and re-opening does not. */
let loadedFor: string | null = null
let volume = 1
/**
 * Master mute for effects: the player's toggle, or the operator's lobby
 * over postMessage. Checked when a shot fires rather than applied as a
 * zero gain, so a muted game schedules no audio nodes at all.
 */
let muted = false

/** The player's master sound switch, or the operator's over postMessage. */
export function setSfxMuted(value: boolean): void {
  muted = value
}

/**
 * Decode a theme's effects.
 *
 * Safe to call repeatedly: the same URL map is loaded once. Failures are
 * per-file, so a broken win sound does not cost the reel stops.
 */
export async function loadSfx(
  key: string,
  urls: Readonly<Record<string, string>>,
  masterVolume: number,
): Promise<void> {
  volume = masterVolume
  if (loadedFor === key) return
  loadedFor = key
  buffers.clear()
  // Warm the context now so the first click does not wait on a decode.
  audioContext()
  await Promise.all(
    Object.entries(urls).map(async ([name, url]) => {
      const buffer = await decodeAudio(url)
      if (buffer) buffers.set(name, buffer)
    }),
  )
}

/** Forget the loaded set — used when the game screen closes. */
export function unloadSfx(): void {
  buffers.clear()
  loadedFor = null
}

/**
 * Fire an effect. Never throws and never awaits — callers are animation
 * paths where a rejected promise would stall the round.
 *
 * `gain` scales this one shot against the theme's master level, so a
 * click can sit under a win fanfare without a second manifest field.
 *
 * `rate` is a playback rate and therefore a pitch shift: 2^(n/12) is n
 * semitones up. One chime file played at rising rates gives the classic
 * ascending win run without shipping a file per step.
 */
export function playSfx(name: SfxName, gain = 1, rate = 1): void {
  if (muted) return
  const buffer = buffers.get(name)
  const ctx = audioContext()
  if (!buffer || !ctx) return
  // A suspended context (no gesture yet) would queue sources that all fire
  // at once on resume. Dropping the shot is the honest behaviour.
  if (ctx.state !== 'running') return

  const source = ctx.createBufferSource()
  source.buffer = buffer
  source.playbackRate.value = rate
  const node = ctx.createGain()
  node.gain.value = volume * gain
  source.connect(node)
  node.connect(ctx.destination)
  source.start()
  // Release the nodes once the shot is over; a slot fires thousands of
  // these per session and the graph would grow without bound.
  source.onended = () => {
    source.disconnect()
    node.disconnect()
  }
}

/**
 * Start a looping effect (the reel-spin bed) and return a stop function.
 *
 * The stop is a short fade, not a cut: the loop ends at an arbitrary point
 * in the waveform, and cutting there clicks. Stopping an effect that never
 * started (no buffer, locked context) is a no-op — callers just always
 * call the returned function.
 */
export function startSfxLoop(name: SfxName, gain = 1): () => void {
  if (muted) return () => {}
  const buffer = buffers.get(name)
  const ctx = audioContext()
  if (!buffer || !ctx || ctx.state !== 'running') return () => {}

  const source = ctx.createBufferSource()
  source.buffer = buffer
  source.loop = true
  const node = ctx.createGain()
  node.gain.value = volume * gain
  source.connect(node)
  node.connect(ctx.destination)
  source.start()

  let stopped = false
  return () => {
    if (stopped) return
    stopped = true
    node.gain.setTargetAtTime(0, ctx.currentTime, 0.05)
    source.stop(ctx.currentTime + 0.3)
    source.onended = () => {
      source.disconnect()
      node.disconnect()
    }
  }
}

/** Reel stops cycle through the variants so three stops do not sound cloned. */
export function playReelStop(index: number): void {
  const variants: SfxName[] = ['reel-stop-1', 'reel-stop-2', 'reel-stop-3']
  // Quiet on purpose: five stops land in under a second every single spin.
  // They are texture, and at full level the mechanism drowns the game.
  playSfx(variants[index % variants.length]!, 0.55)
}
